import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Clock3 } from "lucide-react";
import { Reveal, MaskedLine } from "@/components/Reveal";
import api from "@/lib/api";

const FALLBACK_COVERS = [
  "https://images.unsplash.com/photo-1758518727613-00192aed759b?crop=entropy&cs=srgb&fm=jpg&q=85",
  "https://images.unsplash.com/photo-1758518727820-28491c194bee?crop=entropy&cs=srgb&fm=jpg&q=85",
];

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [category, setCategory] = useState("All");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api
      .get("/blogs", { params: category === "All" ? {} : { category } })
      .then((r) => setPosts(r.data))
      .catch(() => setPosts([]))
      .finally(() => setLoading(false));
  }, [category]);

  const categories = ["All", "Graduate Visa Series", "UK ATS Guide", "Skilled Worker Visa CVs", "Interview Strategy", "UK Market Trends"];

  return (
    <div data-testid="blog-page">
      <section className="mx-auto max-w-7xl px-5 pb-24 pt-20 lg:px-8 lg:pt-28">
        <span className="overline-label">The HR Insight</span>
        <h1 className="mt-5 max-w-3xl font-serif text-4xl font-semibold leading-tight text-slate-50 sm:text-5xl lg:text-6xl" data-testid="blog-headline">
          <MaskedLine delay={0.1}>Notes from inside</MaskedLine>
          <MaskedLine delay={0.3} className="italic text-[#C5A059]">British hiring.</MaskedLine>
        </h1>
        <Reveal delay={0.5}>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-300">
            Field notes from our UK HR team — what ATS platforms actually scan, how British panels score interviews, and how the UK market really hires.
          </p>
        </Reveal>

        <Reveal delay={0.6}>
          <div className="mt-10 flex flex-wrap gap-3" data-testid="blog-category-filters">
            {categories.map((c) => (
              <button
                key={c}
                onClick={() => setCategory(c)}
                data-testid={`blog-filter-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className={`rounded-full border px-5 py-2 text-xs font-semibold uppercase tracking-[0.12em] transition-colors duration-200 ${
                  category === c ? "border-[#C5A059] bg-[#C5A059] text-[#0A0F1D]" : "border-slate-700 text-slate-400 hover:border-slate-500 hover:text-slate-200"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </Reveal>

        <div className="mt-14 grid gap-8 md:grid-cols-2 lg:grid-cols-3">
          {loading ? (
            <p className="col-span-full text-sm text-slate-500" data-testid="blog-loading">Loading articles…</p>
          ) : posts.length === 0 ? (
            <p className="col-span-full text-sm text-slate-500" data-testid="blog-empty">No articles in this category yet — check back soon.</p>
          ) : (
            posts.map((p, i) => (
              <Reveal key={p.id} delay={(i % 3) * 0.08}>
                <Link to={`/blog/${p.slug}`} className="group block h-full" data-testid={`blog-card-${p.slug}`}>
                  <article className="card-surface flex h-full flex-col overflow-hidden transition-colors duration-300 group-hover:border-[#C5A059]/50">
                    <div className="relative h-52 overflow-hidden">
                      <img
                        src={p.cover_image || FALLBACK_COVERS[i % FALLBACK_COVERS.length]}
                        alt={p.title}
                        className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                      <span className="absolute left-4 top-4 rounded-full bg-[#0A0F1D]/85 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.15em] text-[#C5A059] backdrop-blur">
                        {p.category}
                      </span>
                    </div>
                    <div className="flex flex-1 flex-col p-7">
                      <h2 className="font-serif text-xl font-semibold leading-snug text-slate-50 transition-colors group-hover:text-[#C5A059]">{p.title}</h2>
                      <p className="mt-3 flex-1 text-sm leading-relaxed text-slate-400">{p.excerpt}</p>
                      <div className="mt-6 flex items-center justify-between text-xs text-slate-500">
                        <span className="inline-flex items-center gap-1.5 font-mono"><Clock3 size={13} /> {p.read_time} min read</span>
                        <span className="inline-flex items-center gap-1.5 font-semibold text-[#C5A059]">
                          Read <ArrowRight size={13} className="transition-transform duration-300 group-hover:translate-x-1" />
                        </span>
                      </div>
                    </div>
                  </article>
                </Link>
              </Reveal>
            ))
          )}
        </div>
      </section>
    </div>
  );
}
